import React from 'react';
import { FormGroup, Input, InputGroup, InputGroupText } from 'reactstrap';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { FormCheckbox, FormLabel, toasty } from '../common/forms/FormElements';
import moment from 'moment';
import { MomentDateFormat } from '../MomentDateFormat';
import cls from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { util } from '../Util';
import { ApiRoutes } from '../ApiRoutes';
import { ServiceResponseResult } from '../Common';
import { MaxResourceCount } from './LongTermSchedule';

export default class AdditionalEquipmentJobResourceManager
    extends React.Component
{
    constructor(props) {
        super(props);

        this.state = {
            isValid: false,
            additionalJobResources: [],
            additionalLowboyResources: [],
        };
    }

    isValid = (additionalJobResources) =>
        additionalJobResources.every(
            (jr) =>
                !isNaN(jr.count) && jr.count > 0 && jr.count < MaxResourceCount
        );

    onEquipmentTypeSelectionChange = (equipmentTypeSelections) => {
        const { signalChange } = this.props;

        signalChange();

        let { additionalJobResources } = this.state;

        additionalJobResources = additionalJobResources.filter((ajr) =>
            equipmentTypeSelections.some((et) => ajr.equipmentTypeId == et.id)
        );

        const newEquipmentType = equipmentTypeSelections.find(
            (et) =>
                !additionalJobResources.some(
                    (jr) => jr.equipmentTypeId == et.id
                )
        );

        if (newEquipmentType) {
            additionalJobResources.push({
                equipmentTypeId: newEquipmentType.id,
                count: 0,
            });
        }

        this.setState({
            additionalJobResources,
            isValid: this.isValid(additionalJobResources),
        });
    };

    onCountChange = (dataContainer, e) => {
        const { signalChange } = this.props;

        signalChange();

        const { additionalJobResources } = this.state;

        const { equipmentTypeId } = dataContainer;

        additionalJobResources.forEach((ajr) => {
            if (ajr.equipmentTypeId != equipmentTypeId) {
                return;
            }

            ajr.count = e.target.value;
        });

        this.setState({
            additionalJobResources,
            isValid: this.isValid(additionalJobResources),
        });
    };

    getEventDisplayDate = () => {
        const { eventDate } = this.props;

        return !eventDate
            ? null
            : moment(eventDate).format(MomentDateFormat.WeekDayNameAndDate);
    };

    onLowboyScheduleChange = (dataContainer, e) => {
        const { signalChange } = this.props;

        signalChange();

        const { eventDate } = this.props;
        let { additionalLowboyResources } = this.state;

        if (!dataContainer || !e) {
            return;
        }

        const { equipmentTypeId } = dataContainer;

        if (e.target.checked) {
            additionalLowboyResources.push({
                equipmentTypeId,
                date: eventDate,
            });
        } else {
            additionalLowboyResources = additionalLowboyResources.filter(
                (lr) => lr.equipmentTypeId != equipmentTypeId
            );
        }

        this.setState({ additionalLowboyResources });
    };

    onSaveAdditionalJobResources = async () => {
        const { job, eventDate, onResourcesUpdated } = this.props;

        const {
            isSaving,
            isValid,
            additionalJobResources,
            additionalLowboyResources,
        } = this.state;

        if (
            isSaving ||
            !isValid ||
            !additionalJobResources ||
            !additionalJobResources.length ||
            !job ||
            !eventDate ||
            !onResourcesUpdated ||
            !this.isValid(additionalJobResources)
        ) {
            return;
        }

        this.setState({ isSaving: true });

        const response = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.job.addAdditionalResources(),
            {
                jobId: job.id,
                eventDate,
                additionalJobResources,
                additionalLowboyResources,
            },
            'Error Adding Job Resources'
        );

        if (response.result === ServiceResponseResult.Ok) {
            toasty.success('Job resources updated.');

            onResourcesUpdated();

            this.setState({
                additionalJobResources: [],
                additionalLowboyResources: [],
            });
        } else {
            toasty.error('Problem saving the job:', response.message, {
                autoClose: false,
                closeOnClick: true,
            });
        }

        this.setState({ isSaving: false });
    };

    componentDidUpdate(prevProps) {
        const { saving } = this.props;

        if (saving) {
            this.onSaveAdditionalJobResources();
        }
    }

    render() {
        const {
            job,
            eventDate,
            equipmentTypes,
            equipmentTypeCounts,
            dayOfDate,
            lowboyResources,
            equipmentTypeIdsRequiringMoves,
        } = this.props;

        const { additionalJobResources, additionalLowboyResources } =
            this.state;

        equipmentTypes.sort((a, b) => (a.externalId < b.externalId ? -1 : 1));

        additionalJobResources.sort((a, b) => {
            const equipmentTypeA = equipmentTypes.find(
                (et) => et.id == a.equipmentTypeId
            );

            const equipmentTypeB = equipmentTypes.find(
                (et) => et.id == b.equipmentTypeId
            );

            return equipmentTypeA.externalId < equipmentTypeB.externalId
                ? -1
                : 1;
        });

        if (!job || !eventDate || !equipmentTypes) {
            return null;
        }

        return (
            <FormGroup className="additional-equipment-job-resource-manager">
                <FormLabel
                    htmlFor="additionalJobResourceEquipmentTypes"
                    text={`Additional Equipment - ${this.getEventDisplayDate()}`}
                />
                <ValidatedSelect
                    name="additionalJobResourceEquipmentTypes"
                    options={equipmentTypes}
                    getOptionValue={(et) => et.id}
                    getOptionLabel={(et) =>
                        `${et.externalId} - ${et.description}`
                    }
                    isMulti
                    value={
                        equipmentTypes.filter((et) =>
                            additionalJobResources.some(
                                (ajr) => ajr.equipmentTypeId == et.id
                            )
                        ) ?? ''
                    }
                    onChange={this.onEquipmentTypeSelectionChange}
                    hideClear
                />

                {additionalJobResources.map((ajr, i) => {
                    const equipmentType = equipmentTypes.find(
                        (et) => et.id == ajr.equipmentTypeId
                    );

                    const equipmentTypeCount = equipmentTypeCounts.find(
                        (etc) =>
                            etc.equipmentTypeId == ajr.equipmentTypeId &&
                            moment(etc.forDate).isSame(dayOfDate, 'day')
                    );

                    if (!equipmentType) {
                        return null;
                    }

                    const equipmentTypeCanRequireLowboyMove =
                        equipmentTypeIdsRequiringMoves.includes(
                            ajr.equipmentTypeId
                        );

                    const lowboyMoveScheduled = lowboyResources.some(
                        (lr) => lr.equipmentTypeId == ajr.equipmentTypeId
                    );

                    const lowboyMoveChecked =
                        lowboyMoveScheduled ||
                        additionalLowboyResources.some(
                            (lr) => lr.equipmentTypeId == ajr.equipmentTypeId
                        );

                    return (
                        <InputGroup key={ajr.equipmentTypeId} className="mt-2">
                            <InputGroupText>
                                {equipmentType.externalId} -{' '}
                                {equipmentType.description}
                            </InputGroupText>

                            <Input
                                value={ajr.count}
                                className={cls({
                                    invalid: !ajr.count,
                                })}
                                onChange={this.onCountChange.bind(this, {
                                    equipmentTypeId: ajr.equipmentTypeId,
                                })}
                                invalid={
                                    !ajr.count || ajr.count > MaxResourceCount
                                }
                            />

                            <InputGroupText
                                className={cls({
                                    'text-danger':
                                        equipmentTypeCount.availableCount == 0,
                                })}
                            >
                                {equipmentTypeCount.availableCount == 0 && (
                                    <FontAwesomeIcon
                                        icon={faTriangleExclamation}
                                        className="mr-2"
                                    />
                                )}
                                {equipmentTypeCount.availableCount} available
                            </InputGroupText>

                            {equipmentTypeCanRequireLowboyMove && (
                                <div className="row col-md-12 mt-2 mb-2">
                                    {lowboyMoveScheduled && (
                                        <div>
                                            <FontAwesomeIcon
                                                icon={faCheckCircle}
                                                className="mr-2"
                                            />
                                            A lowboy move has already been
                                            scheduled for{' '}
                                            {equipmentType.description}.
                                        </div>
                                    )}

                                    {!lowboyMoveScheduled && (
                                        <FormCheckbox
                                            id={`lowboy-chk-${ajr.equipmentTypeId}`}
                                            checked={lowboyMoveChecked}
                                            name={`lowboy-chk-${ajr.equipmentTypeId}`}
                                            labelText={`Schedule Move for ${equipmentType.description}?`}
                                            onChange={this.onLowboyScheduleChange.bind(
                                                this,
                                                {
                                                    equipmentTypeId:
                                                        ajr.equipmentTypeId,
                                                }
                                            )}
                                        />
                                    )}
                                </div>
                            )}
                        </InputGroup>
                    );
                })}
            </FormGroup>
        );
    }
}
